import React, { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./ChangePassword.css";
import "./SignUp.css";
import "./ResetPassword.css";
import ResetLogo from "../../Components/Assets/images/logo.png";
import { ChevronLeft, Eye, EyeOff } from "lucide-react";
import { useTitle } from "../../hooks/useTitle";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const validatePassword = (pw) =>
  /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(pw);

const WORKER = "https://email-api.trendbayds.com";

// ── Confirm new password form (token + userId in URL) ────────────────────────

function PasswordResetForm({ token, userId }) {
  useTitle("Set New Password - TrendBay DS");
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="reset-password-container">
        <header className="reset-password-header">
          <img src={ResetLogo} alt="TrendBay Logo" className="reset_logo" />
        </header>
        <main className="reset-password-main text-center">
          <h2>Password Reset!</h2>
          <p>Your password has been updated. <Link to="/login" style={{ color: "#00A855" }}>Log in</Link></p>
        </main>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (!validatePassword(newPassword)) {
      setError("Password must have at least 8 characters, 1 uppercase letter, 1 number & 1 symbol.");
      return;
    }
    if (!WORKER) { setError("Service not configured. Please try again later."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${WORKER}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, userId, newPassword }),
      });
      const data = await res.json();
      if (!data.ok) {
        setError(data.error || "Failed to reset password. The link may have expired.");
        return;
      }
      setDone(true);
    } catch {
      setError("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <header className="reset-password-header">
        <img src={ResetLogo} alt="TrendBay Logo" className="reset_logo" />
      </header>
      <main className="reset-password-main">
        <Link to="/login" className="reset_back-link float-left">
          <ChevronLeft size={15} /> Back to Log in
        </Link>
        <h2>Create a New Password</h2>
        <div className="reset_container">
          <form onSubmit={handleSubmit} className="reset-password-form">
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"}
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                required
              />
              <span
                onClick={() => setShowPw((p) => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#888" }}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </span>
            </div>
            <div style={{ position: "relative" }}>
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                required
              />
              <span
                onClick={() => setShowConfirm((p) => !p)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#888" }}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </span>
            </div>
            <button type="submit" disabled={loading}>
              {loading ? "Resetting…" : "Reset Password"}
            </button>
          </form>
        </div>
        {error && <div className="error">{error}</div>}
      </main>
    </div>
  );
}

// ── Request reset form (no token in URL) ─────────────────────────────────────

function RequestResetForm() {
  useTitle("Reset Password - TrendBay DS");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!WORKER) { setError("Service not configured. Please try again later."); return; }
    setLoading(true);
    setError("");
    try {
      await fetch(`${WORKER}/initiate-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), appType: "buyer" }),
      });
      setSent(true);
    } catch {
      // Always show success to prevent enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="change-password mt-5">
        <header className="change-password-header">
          <img src={ResetLogo} alt="TrendBay Logo" className="change_logo" />
        </header>
        <main className="change-password-main" style={{ textAlign: "center" }}>
          <h2>Check your inbox</h2>
          <p style={{ color: "#555", marginBottom: 20 }}>
            If an account with <strong>{email}</strong> exists, we sent a reset link. Check your spam folder too.
          </p>
          <Link to="/login" style={{ color: "#00A855", fontWeight: 600 }}>Back to Sign In</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="change-password mt-5">
      <header className="change-password-header">
        <img src={ResetLogo} alt="TrendBay Logo" className="change_logo" />
      </header>
      <main className="change-password-main">
        <Link to="/login" className="change-link float-left">
          <ChevronLeft size={15} style={{ verticalAlign: "middle" }} /> Back to Log in / Sign Up
        </Link>
        <h2>Reset Your Password</h2>
        <div className="change_container">
          <p>Please enter the email address associated with your TrendBay account.</p>
          <form onSubmit={handleSubmit} className="reset-password-form">
            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
          </form>
        </div>
        {error && <div className="error">{error}</div>}
      </main>
    </div>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────

const ResetPassword = () => {
  const query = useQuery();
  const token = query.get("token");
  const userId = query.get("userId");

  if (token && userId) {
    return <PasswordResetForm token={token} userId={userId} />;
  }
  return <RequestResetForm />;
};

export default ResetPassword;
